/**
 * Payment card encryption — RSA-OAEP-AES-256-GCM
 *
 * Algorithm:
 *  - Random 32-byte AES key + 12-byte IV per transaction
 *  - PAN and CVV encrypted separately with AES-256-GCM (same key + IV)
 *  - AES key wrapped with RSA-OAEP (SHA-256) using backend public key
 *
 * Output shape merges into payment_collection_info:
 *  { encryption, encrypted_aes_key, iv, card_number_enc, card_sec_code_enc }
 *  card_number and card_sec_code are NOT sent.
 */

export type PaymentEncryptionAlgorithm = "RSA-OAEP-AES-256-GCM";

export type EncryptedCardPaymentFields = {
  encryption: {
    version: 1;
    key_id: string;
    algorithm: PaymentEncryptionAlgorithm;
  };
  encrypted_aes_key: string;
  iv: string;
  card_number_enc: string;
  card_sec_code_enc: string;
};

/** Use globalThis.crypto — bare `crypto` may not resolve in webpack bundles. */
function getWebCrypto(): Crypto {
  const webCrypto = globalThis.crypto;
  if (!webCrypto?.subtle) {
    throw new Error(
      "Payment encryption requires a secure context with Web Crypto (globalThis.crypto.subtle). " +
        "Use HTTPS or http://localhost."
    );
  }
  return webCrypto;
}

function getSubtleCrypto(): SubtleCrypto {
  return getWebCrypto().subtle;
}

function arrayBufferToBase64(buf: ArrayBuffer | Uint8Array): string {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function pemToSpki(pem: string): ArrayBuffer {
  const b64 = pem
    .replace(/-----BEGIN PUBLIC KEY-----/g, "")
    .replace(/-----END PUBLIC KEY-----/g, "")
    .replace(/\s/g, "");
  if (!b64) {
    throw new Error("Invalid public key PEM: no base64 content found.");
  }
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

async function importRsaPublicKey(pem: string): Promise<CryptoKey> {
  try {
    return await getSubtleCrypto().importKey(
      "spki",
      pemToSpki(pem),
      { name: "RSA-OAEP", hash: "SHA-256" },
      false,
      ["encrypt"]
    );
  } catch (err) {
    const detail = err instanceof Error ? err.message : String(err);
    throw new Error(`Failed to import payment encryption public key: ${detail}`);
  }
}

async function aesGcmEncrypt(
  plaintext: string,
  aesKey: CryptoKey,
  iv: Uint8Array
): Promise<string> {
  const encoded = new TextEncoder().encode(plaintext);
  try {
    const cipherBuffer = await getSubtleCrypto().encrypt(
      { name: "AES-GCM", iv: iv as BufferSource, tagLength: 128 },
      aesKey,
      encoded
    );
    return arrayBufferToBase64(cipherBuffer);
  } catch (err) {
    const detail = err instanceof Error ? err.message : String(err);
    throw new Error(`AES-GCM encryption failed: ${detail}`);
  }
}

async function wrapAesKeyWithRsa(
  rsaPublicKey: CryptoKey,
  aesKey: CryptoKey
): Promise<string> {
  try {
    const rawAes = await getSubtleCrypto().exportKey("raw", aesKey);
    const wrapped = await getSubtleCrypto().encrypt(
      { name: "RSA-OAEP" },
      rsaPublicKey,
      rawAes
    );
    return arrayBufferToBase64(wrapped);
  } catch (err) {
    const detail = err instanceof Error ? err.message : String(err);
    throw new Error(`RSA key wrap failed: ${detail}`);
  }
}

export function isWebCryptoAvailable(): boolean {
  return !!globalThis.crypto?.subtle;
}

export function assertWebCryptoAvailable(): void {
  getWebCrypto();
}

/** Legacy fallback when REACT_APP_ENABLE_ENCRYPTION=false (dev / rollback only). */
export function buildLegacyCardPaymentFields(input: {
  cardNumber: string;
  cardSecCode: string;
}): { card_number: string; card_sec_code: string } {
  const pan = input.cardNumber.replace(/\D/g, "");
  return {
    card_number: btoa(pan),
    card_sec_code: input.cardSecCode,
  };
}

export async function encryptCardPaymentFields(input: {
  cardNumber: string;
  cardSecCode: string;
  publicKeyPem: string;
  keyId: string;
}): Promise<EncryptedCardPaymentFields> {
  assertWebCryptoAvailable();

  const iv = getWebCrypto().getRandomValues(new Uint8Array(12));

  const aesKey = await getSubtleCrypto().generateKey(
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt"]
  );

  const rsaPublicKey = await importRsaPublicKey(input.publicKeyPem);

  const pan = input.cardNumber.replace(/\D/g, "");
  const cvv = input.cardSecCode.replace(/\D/g, "");

  if (!pan) {
    throw new Error("Card number is required for encryption.");
  }
  if (!cvv) {
    throw new Error("Card security code is required for encryption.");
  }

  // Encrypt sequentially — some browsers reject concurrent subtle ops on one key+IV
  const card_number_enc = await aesGcmEncrypt(pan, aesKey, iv);
  const card_sec_code_enc = await aesGcmEncrypt(cvv, aesKey, iv);
  const encrypted_aes_key = await wrapAesKeyWithRsa(rsaPublicKey, aesKey);

  return {
    encryption: {
      version: 1,
      key_id: input.keyId,
      algorithm: "RSA-OAEP-AES-256-GCM",
    },
    encrypted_aes_key,
    iv: arrayBufferToBase64(iv),
    card_number_enc,
    card_sec_code_enc,
  };
}
